import { computed, onMounted, ref, watchEffect } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useMatchingSettings from '@/features/matching/hooks/use-matching-settings.hook'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useUser from '@/features/users/hooks/use-user.hook'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'

export const MATCHING_CRITERIA_LOCAL_STORAGE_KEY = 'saved-matching-information'

export type MatchingTemplateFieldType = { uuid: string; name: string }
export type MatchingTemplateRoleType = 'mentor' | 'mentee'

const MATCHING_CRITERIA_REGEX = /%\{([^}]+)\}%\[([0-9a-fA-F-]{36})\]/g

/** Champs d'un template de matching, dans l'ordre d'apparition (nom affiché + uuid). */
export function extractTemplateFields(template: string): MatchingTemplateFieldType[] {
  const fields: MatchingTemplateFieldType[] = []
  let match
  const regex = new RegExp(MATCHING_CRITERIA_REGEX.source, 'g')
  while ((match = regex.exec(template)) !== null) {
    fields.push({ name: match[1], uuid: match[2] })
  }
  return fields
}

// Brouillon de saisie partagé entre toutes les instances du hook : les inputs
// sont rendus par v-html dans le conteneur, alors que le titre (compte des
// réponses restantes) et le bouton de validation vivent dans le composant
// parent. Clé = uuid du champ. Réinitialisé à chaque (re)rendu du formulaire.
const criteriaDraftRef = ref<Record<string, string>>({})

export default function useMatchingInformation({
  onSubmitCallback,
}: {
  onSubmitCallback: (filledCriteria: { inputId: string; value: string }[]) => void
}) {
  // Extrait les UUIDs d'un template de matching
  function extractUUIDs(template: string): string[] {
    return extractTemplateFields(template).map((field) => field.uuid)
  }

  function escapeHtmlAttr(value: string) {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  function prepareMatchingInformationHTML(template: string, filledCriteria: { inputId: string; value: string }[]) {
    let result: string = ''
    const required = requireAllFieldsRef.value

    // Remplace les reponses de critères de matching par des inputs
    result = template.replace(MATCHING_CRITERIA_REGEX, (match, fieldName, uuid) => {
      const name = escapeHtmlAttr(fieldName)
      // Champ obligatoire : astérisque dans le placeholder + aria-required pour
      // les lecteurs d'écran. Pas d'attribut `required` natif : la validation
      // reste la nôtre (message traduit qui nomme les champs manquants).
      const requiredAttrs = required ? ' aria-required="true"' : ''
      const placeholder = required ? `${name} *` : name
      return `<input type="text" id="matching-criteria-${uuid}" name="${name}" data-uuid="${uuid}" placeholder="${placeholder}" aria-label="${name}"${requiredAttrs}>`
    })

    // Ajoute les valeurs des inputs si elles sont déjà remplies
    if (filledCriteria) {
      for (const { inputId, value } of filledCriteria) {
        const regex = new RegExp(`(<input[^>]*data-uuid="${inputId}"[^>]*)(>)`, 'i')
        result = result.replace(regex, `$1 value="${escapeHtmlAttr(value)}"$2`)
      }
    }
    return result
  }

  // function de faire une phrase complète avec les critère de matching et le template
  function fillTemplate(
    template: string,
    criteria: {
      inputId: string
      value: string
    }[],
  ): string {
    if (!template || !criteria) {
      return ''
    }
    const uuidMap = new Map<string, string>()
    for (const { inputId, value } of criteria) {
      uuidMap.set(inputId, value)
    }

    // Regex qui capture tout le bloc %{...}%[...]
    // Le groupe 1 correspond à l'UUID
    const regex = /%\{[^}]+\}%\[([0-9a-fA-F-]{36})\]/g

    return template.replace(regex, (match, uuid) => {
      const val = uuidMap.get(uuid)
      return val !== undefined ? val : ''
    })
  }

  const isSubmittingRef = ref(false)
  const savedCriteriaRef = ref<{ inputId: string; value: string }[] | null>(null)

  const { t } = useI18n()
  const toast = useToast()

  const { accountRef } = useAccount()

  const accountIdRef = computed(() => accountRef.value?.id)
  const { userRef } = useUser(accountIdRef)
  const serverCriteriaRef = computed(() => userRef.value?.matchingCriteria)
  const userCriteriaRef = computed(() => serverCriteriaRef.value || savedCriteriaRef.value)

  const mentorMatchingTemplateRef = ref<string | null>(null)
  const menteeMatchingTemplateRef = ref<string | null>(null)
  const currentUserMatchingTemplateRef = ref<string | null>(null)
  // Rôle auquel correspond le template courant (et non le rôle de l'utilisateur :
  // un mentor peut éditer ses critères mentee depuis le profil, et inversement).
  const currentTemplateRoleRef = ref<MatchingTemplateRoleType | null>(null)

  const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
  const { canAutoMatch } = useMatchingSettings(selectedProgramIdRef)

  // Critères filtrés par rôle (basés sur les UUIDs des templates).
  // Le filtre par programId est indispensable : user.matchingCriteria contient les
  // réponses de TOUS les programmes, et des templates copiés entre programmes ont pu
  // partager les mêmes inputIds (données antérieures à la migration d'unicité).
  const mentorCriteriaRef = computed(() => {
    if (!serverCriteriaRef.value || !mentorMatchingTemplateRef.value) return null
    const mentorUUIDs = extractUUIDs(mentorMatchingTemplateRef.value)
    return serverCriteriaRef.value.filter(
      (c) => mentorUUIDs.includes(c.inputId) && (!c.programId || c.programId === selectedProgramIdRef.value),
    )
  })

  const menteeCriteriaRef = computed(() => {
    if (!serverCriteriaRef.value || !menteeMatchingTemplateRef.value) return null
    const menteeUUIDs = extractUUIDs(menteeMatchingTemplateRef.value)
    return serverCriteriaRef.value.filter(
      (c) => menteeUUIDs.includes(c.inputId) && (!c.programId || c.programId === selectedProgramIdRef.value),
    )
  })
  // Un profil n'est appariable que si CHAQUE champ du template a une valeur :
  // un import partiel (ex. 2 critères sur 6) ne doit pas passer pour complet.
  function isCriteriaComplete(template: string | null, criteria: { inputId: string; value: string }[] | null) {
    if (!template) return false
    const filled = new Set((criteria ?? []).filter((c) => c.value?.trim()).map((c) => c.inputId))
    return extractUUIDs(template).every((uuid) => filled.has(uuid))
  }

  const isMentorCriteriaCompleteRef = computed(() =>
    isCriteriaComplete(mentorMatchingTemplateRef.value, mentorCriteriaRef.value),
  )
  const isMenteeCriteriaCompleteRef = computed(() =>
    isCriteriaComplete(menteeMatchingTemplateRef.value, menteeCriteriaRef.value),
  )

  const { programRolesRef } = useProgramRoles({ programIdRef: selectedProgramIdRef })

  // Admin, manager ou support sur le programme : jamais soumis à la saisie
  // obligatoire ni à la popup sans sortie, même s'ils y sont aussi mentor
  // (ils pilotent le programme, ils ne sont pas la cible de la relance).
  const isStaffRef = computed(
    () =>
      programRolesRef.value?.some((role) =>
        [RoleEnumTypeV2.ADMIN, RoleEnumTypeV2.MANAGER, RoleEnumTypeV2.SUPPORT].includes(role),
      ) ?? false,
  )

  // Règle d'obligation : tous les champs du template sont requis dès que la
  // personne ne peut pas lancer le matching elle-même. Un mentor ne le lance
  // jamais ; un mentee ne le lance que si la suggestion automatique est active.
  // Sinon (mentee en auto-match), la règle historique « au moins un champ »
  // s'applique : la qualité du résultat est sous ses yeux.
  const requireAllFieldsRef = computed(() => {
    if (isStaffRef.value) return false
    if (currentTemplateRoleRef.value === 'mentor') return true
    if (currentTemplateRoleRef.value === 'mentee') return !canAutoMatch.value
    return false
  })

  const currentTemplateFieldsRef = computed(() =>
    currentUserMatchingTemplateRef.value ? extractTemplateFields(currentUserMatchingTemplateRef.value) : [],
  )

  const currentPrefilledCriteriaRef = computed(() =>
    currentTemplateRoleRef.value === 'mentee' ? menteeCriteriaRef.value : mentorCriteriaRef.value,
  )

  function getFieldValue(uuid: string): string {
    const draft = criteriaDraftRef.value[uuid]
    if (draft !== undefined) return draft
    return currentPrefilledCriteriaRef.value?.find((c) => c.inputId === uuid)?.value ?? ''
  }

  /** Champs du template courant encore vides (brouillon de saisie inclus). */
  const missingFieldsRef = computed(() => currentTemplateFieldsRef.value.filter((f) => !getFieldValue(f.uuid).trim()))
  const totalFieldsCountRef = computed(() => currentTemplateFieldsRef.value.length)
  const remainingFieldsCountRef = computed(() => missingFieldsRef.value.length)

  /** À brancher en @input sur le conteneur des inputs (délégation d'événement). */
  function onCriteriaInput(event: Event) {
    const input = event.target as HTMLInputElement | null
    const uuid = input?.dataset?.uuid
    if (!input || !uuid) return
    criteriaDraftRef.value = { ...criteriaDraftRef.value, [uuid]: input.value }
    if (input.value.trim()) input.removeAttribute('aria-invalid')
  }

  function resetCriteriaDraft() {
    criteriaDraftRef.value = {}
  }

  const { programRef: selectedProgramRef } = useProgram(selectedProgramIdRef)

  onMounted(() => {
    try {
      const stored = window.localStorage.getItem(MATCHING_CRITERIA_LOCAL_STORAGE_KEY)
      if (stored) {
        savedCriteriaRef.value = JSON.parse(stored)
      }
    } catch (e) {
      toast?.error(t('error.common'))
    }
  })

  // get the matching information from the program
  watchEffect(() => {
    if (selectedProgramRef.value) {
      mentorMatchingTemplateRef.value = selectedProgramRef.value.settings.mentorMatchingCriteria ?? null
      menteeMatchingTemplateRef.value = selectedProgramRef.value.settings.menteeMatchingCriteria ?? null

      // Si le modal new-matching ou mentee-matching est ouvert, c'est toujours un mentee qui cherche un mentor
      if (modalsStore.modals['new-matching'].isOpen || modalsStore.modals['mentee-matching'].isOpen) {
        currentTemplateRoleRef.value = 'mentee'
      }
      // Si le modal mentor-matching est ouvert, c'est un mentor qui remplit ses critères
      else if (modalsStore.modals['mentor-matching'].isOpen) {
        currentTemplateRoleRef.value = 'mentor'
      }
      // Si le modal program-criterias est ouvert avec un rôle spécifié
      else if (modalsStore.modals['program-criterias'].isOpen) {
        const role = (modalsStore.modals['program-criterias'].attachedData as { role?: MatchingTemplateRoleType })?.role
        currentTemplateRoleRef.value = role === 'mentee' ? 'mentee' : 'mentor'
      }
      // Sinon, on se base sur le rôle de l'utilisateur dans le programme
      else {
        currentTemplateRoleRef.value = programRolesRef.value?.includes(RoleEnumTypeV2.MENTOR) ? 'mentor' : 'mentee'
      }

      currentUserMatchingTemplateRef.value =
        currentTemplateRoleRef.value === 'mentee' ? menteeMatchingTemplateRef.value : mentorMatchingTemplateRef.value
    }
  })

  const onSubmit = (e: Event) => {
    e.preventDefault()
    if (!currentUserMatchingTemplateRef.value) return

    // extraire les valeurs des inputs
    const inputs = Array.from(document.querySelectorAll<HTMLInputElement>(`input[data-uuid]`))
    const filledCriteria: { inputId: string; value: string }[] = []
    let hasAtLeastOneFilledField = false

    inputs.forEach((input) => {
      const value = input.value.trim()
      if (value) {
        hasAtLeastOneFilledField = true
      }
      input.removeAttribute('aria-invalid')
      filledCriteria.push({ inputId: input.dataset.uuid!, value: input.value })
    })

    if (requireAllFieldsRef.value) {
      // Tous les champs sont obligatoires : on nomme ceux qui manquent et on
      // pose le focus sur le premier.
      const missingInputs = inputs.filter((input) => !input.value.trim())
      if (missingInputs.length > 0) {
        missingInputs.forEach((input) => input.setAttribute('aria-invalid', 'true'))
        missingInputs[0].focus()
        toast?.error(
          t('matching.fillingCriterias.validation.allFieldsRequired', {
            fields: missingInputs.map((input) => input.name).join(', '),
          }),
        )
        return
      }
    } else if (!hasAtLeastOneFilledField) {
      // Vérifier qu'au moins un champ est rempli
      toast?.error(t('matching.fillingCriterias.validation.atLeastOneField'))
      return
    }

    onSubmitCallback(filledCriteria)
    try {
      window.localStorage.setItem(MATCHING_CRITERIA_LOCAL_STORAGE_KEY, JSON.stringify(filledCriteria))
      savedCriteriaRef.value = filledCriteria
    } catch (error) {
      toast?.error(t('error.common'))
    }
  }

  return {
    prepareMatchingInformationHTML,
    userCriteriaRef,
    mentorCriteriaRef,
    menteeCriteriaRef,
    isMentorCriteriaCompleteRef,
    isMenteeCriteriaCompleteRef,
    mentorMatchingTemplateRef,
    menteeMatchingTemplateRef,
    currentUserMatchingTemplateRef,
    currentTemplateRoleRef,
    isStaffRef,
    requireAllFieldsRef,
    missingFieldsRef,
    remainingFieldsCountRef,
    totalFieldsCountRef,
    onCriteriaInput,
    resetCriteriaDraft,
    isSubmittingRef,
    onSubmit,
    fillTemplate,
    extractUUIDs,
  }
}
